'use client';

import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { LegendItem } from './LegendItem';
import type { LegendEntry } from '@/components/map/MapChart';
import taxonomyData from '../../../public/taxonomy.json';

interface Props {
  entries: LegendEntry[];
  hiddenSeries: Set<string>;
  onToggle: (name: string) => void;
  onZoom: (name: string) => void;
}

const TAXONOMY_GROUPS = taxonomyData.groups as Record<string, string[]>;

const SUBCATEGORY_TO_GROUP: Record<string, string> = {};
Object.entries(TAXONOMY_GROUPS).forEach(([group, subcats]) => {
  subcats.forEach(s => { SUBCATEGORY_TO_GROUP[s] = group; });
});

export const ChartLegend: React.FC<Props> = ({ entries, hiddenSeries, onToggle, onZoom }) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const toggleGroup = (group: string): void => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group); else next.add(group);
      return next;
    });
  };

  const byGroup: Record<string, LegendEntry[]> = {};
  const ungrouped: LegendEntry[] = [];
  const uncategorized = entries.find(e => e.name === 'Uncategorized');

  entries.forEach(e => {
    if (e.name === 'Uncategorized') return;
    const group = SUBCATEGORY_TO_GROUP[e.name];
    if (group) {
      if (!byGroup[group]) byGroup[group] = [];
      byGroup[group].push(e);
    } else {
      ungrouped.push(e);
    }
  });

  return (
    <ul className="flex-1 overflow-y-auto py-[6px] legend-scroll list-none p-0 m-0">
      {Object.keys(TAXONOMY_GROUPS).map(group => {
        const groupEntries = byGroup[group];
        if (!groupEntries?.length) return null;
        const isCollapsed = collapsedGroups.has(group);
        return (
          <React.Fragment key={group}>
            <button
              type="button"
              onClick={() => toggleGroup(group)}
              aria-expanded={!isCollapsed}
              className="w-full flex items-center gap-[5px] px-[14px] pt-[10px] pb-[4px] mt-[2px] first:pt-[6px] first:mt-0 cursor-pointer group"
            >
              {isCollapsed
                ? <ChevronRight size={9} className="text-text-muted flex-shrink-0" aria-hidden="true" />
                : <ChevronDown size={9} className="text-text-muted flex-shrink-0" aria-hidden="true" />
              }
              <span className="text-[9px] font-semibold tracking-[0.12em] uppercase text-text-muted group-hover:text-text-secondary transition-colors">
                {group}
              </span>
            </button>
            {!isCollapsed && groupEntries.map(e => (
              <LegendItem
                key={e.name}
                name={e.name}
                color={e.color}
                count={e.count}
                isHidden={hiddenSeries.has(e.name)}
                onToggle={onToggle}
                onZoom={onZoom}
              />
            ))}
          </React.Fragment>
        );
      })}

      {ungrouped.length > 0 && ungrouped.map(e => (
        <LegendItem
          key={e.name}
          name={e.name}
          color={e.color}
          count={e.count}
          isHidden={hiddenSeries.has(e.name)}
          onToggle={onToggle}
          onZoom={onZoom}
        />
      ))}

      {uncategorized && (
        <>
          <div className="text-[9px] font-semibold tracking-[0.12em] uppercase text-text-muted px-[14px] pt-[10px] pb-[4px]">
            Other
          </div>
          <LegendItem
            name={uncategorized.name}
            color={uncategorized.color}
            count={uncategorized.count}
            isHidden={hiddenSeries.has('Uncategorized')}
            onToggle={onToggle}
            onZoom={onZoom}
          />
        </>
      )}
    </ul>
  );
};
